import * as React from "react";
import {
  Toolbar,
  QuickFilter,
  QuickFilterControl,
  QuickFilterClear,
  ToolbarButton,
  FilterPanelTrigger,
  GridFilterModel,
} from "@mui/x-data-grid";
import { FiX, FiSearch } from "react-icons/fi";
import { IoFilterSharp } from "react-icons/io5";

import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { Tooltip, Box } from "@mui/material";

interface DataGridToolbarProps {
  filterModel?: GridFilterModel;
}

export function DataGridToolbar({ filterModel }: DataGridToolbarProps) {
  // Check if any filters are active (excluding quick filter)
  const hasActiveFilters = React.useMemo(() => {
    if (!filterModel?.items) return false;

    return filterModel.items.some(item => {
      const value = item.value;

      // Handle null/undefined
      if (value === null || value === undefined) return false;

      // Handle empty strings
      if (typeof value === "string" && value.trim() === "") return false;

      // Handle empty arrays
      if (Array.isArray(value) && value.length === 0) return false;

      // Handle arrays with only null/undefined/empty values
      if (Array.isArray(value)) {
        return value.some(
          v =>
            v !== null &&
            v !== undefined &&
            (typeof v !== "string" || v.trim() !== ""),
        );
      }

      // For other types, consider them active if they have a value
      return true;
    });
  }, [filterModel]);

  return (
    <Toolbar>
      <QuickFilter expanded>
        <QuickFilterControl
          render={({ ref, ...other }) => (
            <TextField
              {...other}
              sx={{ width: 260 }}
              inputRef={ref}
              aria-label="Search"
              placeholder="Search..."
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FiSearch />
                    </InputAdornment>
                  ),
                  endAdornment: other.value ? (
                    <InputAdornment position="end">
                      <QuickFilterClear
                        edge="end"
                        size="small"
                        aria-label="Clear search"
                        material={{ sx: { marginRight: -0.75 } }}
                      >
                        <FiX />
                      </QuickFilterClear>
                    </InputAdornment>
                  ) : null,
                  ...other.slotProps?.input,
                },
                ...other.slotProps,
              }}
            />
          )}
        />
      </QuickFilter>
      <Tooltip title="Filters">
        <Box sx={{ position: "relative" }}>
          <FilterPanelTrigger render={<ToolbarButton />}>
            <IoFilterSharp />
          </FilterPanelTrigger>
          {hasActiveFilters && (
            <Box
              sx={{
                position: "absolute",
                top: -2,
                right: -2,
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "primary.main",
                border: "1px solid white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }}
            />
          )}
        </Box>
      </Tooltip>
      <Box sx={{ flexGrow: 1 }} />
    </Toolbar>
  );
}
